import React from "react";
import { observer } from "mobx-react";
import { Grid, Box } from "@mui/material";
import { useRootStore } from "../../../../store/contexts";
import { GroupContainer } from "../../../../common/components";
import { FormToolbox } from "../../../../common/controls/forms/FormToolbox";
import { FormSaveFooter } from "../../../../common/controls/forms/FormSaveFooter";

import FormExtras from "../../../../common/components/formExtras";
import { ShowcaseLineCreateStore } from "../../store";
import { InputString } from "../../../common/controls";
import { InputNumber } from "../../../common/controls";
import { FormControl } from "@mui/material";

export type ShowcaseLineCreateFormProps = {
  onSave?: () => void;
  onCancel?: () => void;
  /** Mientras se guarda, Guardar queda deshabilitado. */
  saving?: boolean;
  showToolbox?: boolean;
  showSaveFooter?: boolean;
  conceptSelectedValue?: { displayId: string; id: string } | null;
  onConceptChange?: (value: string) => void;
  concept: string;
  conceptError?: string;
  quantitySelectedValue?: { displayId: string; id: string } | null;
  onQuantityChange?: (value: number) => void;
  quantity: number;
  quantityError?: string;
  unitPriceSelectedValue?: { displayId: string; id: string } | null;
  onUnitPriceChange?: (value: number) => void;
  unitPrice: number;
  unitPriceError?: string;
  noteSelectedValue?: { displayId: string; id: string } | null;
  onNoteChange?: (value: string) => void;
  note: string;
  noteError?: string;
  showcaseLineCreateStore: ShowcaseLineCreateStore;
};

export const ShowcaseLineCreateForm: React.FC<ShowcaseLineCreateFormProps> =
  observer(
    ({
      onSave = () => {},
      onCancel = () => {},
      saving = false,
      showToolbox = true,
      showSaveFooter,
      concept = "",
      conceptError,
      onConceptChange = () => {},
      quantity = 0,
      quantityError,
      onQuantityChange = () => {},
      unitPrice = 0,
      unitPriceError,
      onUnitPriceChange = () => {},
      note = "",
      noteError,
      onNoteChange = () => {},
      showcaseLineCreateStore,
    }) => {
      const rootStore = useRootStore();
      const { langStore } = rootStore;

      return (
        <Grid>
          {showToolbox && (
            <FormToolbox
              saveAndCloseLabel={langStore.saveEntity}
              closeLabel={langStore.cancelNewEntity}
              saveForm={() => onSave()}
              saveDisabled={saving}
              closeForm={() => onCancel()}
            />
          )}
          <Box>
            <FormExtras
              entity="ShowcaseLine"
              mode="create"
              values={{}}
              options={{}}
              onPatch={(campo: string, valor: any) => {
                switch (campo) {
                  case "concept":
                    onConceptChange?.(valor);
                    break;
                  case "quantity":
                    onQuantityChange?.(valor);
                    break;
                  case "unitPrice":
                    onUnitPriceChange?.(valor);
                    break;
                  case "note":
                    onNoteChange?.(valor);
                    break;
                }
              }}
            />
            <GroupContainer
              id="null"
              errorText={showcaseLineCreateStore.getCardErrors("null")}
              title={langStore.groupShowcaseLineLabel}
              collapsible={false}
            >
              <FormControl fullWidth>
                <InputString
                  autoFocus
                  value={concept}
                  error={Boolean(conceptError)}
                  warning={
                    !conceptError &&
                    Boolean(showcaseLineCreateStore.warnings["concept"])
                  }
                  helperText={
                    conceptError ||
                    showcaseLineCreateStore.warnings["concept"]?.message
                  }
                  label={langStore.ShowcaseLineConceptLabel}
                  onChange={(value: string) => onConceptChange(value)}
                />
                <InputNumber
                  value={quantity}
                  error={Boolean(quantityError)}
                  warning={
                    !quantityError &&
                    Boolean(showcaseLineCreateStore.warnings["quantity"])
                  }
                  helperText={
                    quantityError ||
                    showcaseLineCreateStore.warnings["quantity"]?.message
                  }
                  allowNegative={false}
                  label={langStore.ShowcaseLineQuantityLabel}
                  decimalScale={0}
                  onChange={(value: number) => onQuantityChange(value)}
                />
                <InputNumber
                  value={unitPrice}
                  error={Boolean(unitPriceError)}
                  warning={
                    !unitPriceError &&
                    Boolean(showcaseLineCreateStore.warnings["unitPrice"])
                  }
                  helperText={
                    unitPriceError ||
                    showcaseLineCreateStore.warnings["unitPrice"]?.message
                  }
                  allowNegative={true}
                  label={langStore.ShowcaseLineUnitPriceLabel}
                  decimalScale={2}
                  prefix={"$"}
                  thousandSeparator={","}
                  fixedDecimalScale={true}
                  onChange={(value: number) => onUnitPriceChange(value)}
                />

                <InputString
                  value={note}
                  error={Boolean(noteError)}
                  warning={
                    !noteError &&
                    Boolean(showcaseLineCreateStore.warnings["note"])
                  }
                  helperText={
                    noteError ||
                    showcaseLineCreateStore.warnings["note"]?.message
                  }
                  label={langStore.ShowcaseLineNoteLabel}
                  onChange={(value: string) => onNoteChange(value)}
                  nullText={langStore.optionalFieldHint}
                />
              </FormControl>
            </GroupContainer>
          </Box>
          {(showSaveFooter ?? !showToolbox) && (
            <FormSaveFooter
              onSave={() => onSave()}
              label={langStore.saveEntity}
              disabled={saving}
            />
          )}
        </Grid>
      );
    }
  );
